import { useCallback } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

import { editParams } from 'SRC/hooks/searchParams/editParams';
import {
  FilterParamsConfig,
  FilterParamTypes,
} from 'SRC/hooks/searchParams/types';

export function useParamOperations<
  P extends FilterParamsConfig | FilterParamTypes,
>(setter: SetURLSearchParams, config: P) {
  const editParamsCallback = useCallback(
    (
      params: P extends FilterParamTypes
        ? P extends 'array'
          ? Record<string, string[]>
          : Record<string, string>
        : Partial<Record<keyof P, string | string[] | undefined | null>>,
    ) => {
      setter((prevParams) => {
        const paramsCopy = new URLSearchParams(prevParams);
        editParams(params, paramsCopy, config);

        return paramsCopy;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config is a constant
    [setter],
  );

  const removeParams = useCallback(
    (
      params: P extends FilterParamTypes
        ? P extends 'array'
          ? string[]
          : string
        : keyof P | keyof P[],
    ) => {
      setter((prevParams) => {
        const paramsCopy = new URLSearchParams(prevParams);

        if (Array.isArray(params)) {
          params.forEach((param) => paramsCopy.delete(param));
        } else {
          paramsCopy.delete(params as string);
        }

        return paramsCopy;
      });
    },
    [setter],
  );

  return {
    addParams: editParamsCallback,
    removeParams,
  };
}
